import { FocusCss } from '@tvg/fuse-utils';
import { renderToStaticMarkup } from 'react-dom/server';
import styled from 'styled-components';
import {
	GridContainer,
	GridItem,
	GridRow,
	gridConfig
} from '../../fuse-responsive-grid/src/grid'
import { TappableArea as TappableAreaCss } from '../../fuse-tappable-area/src/tappablearea';
import { defaultColumnGaps, isColumnGapSizeValid } from './config';

const CarouselContainer = styled.div.attrs({
	className: 'fuse-carousel',
})`
	display: flex;
	flex-direction: column;

	/* Don't need button navigation */
	@supports (scroll-snap-type) {
		.slider > a {
			display: none;
		}
	}
`;

const OverflowContainer = styled.div
	.withConfig({
		shouldForwardProp: (prop) => !['touchPeek', 'chevronOverlay'].includes(prop),
	})
	.attrs({
		className: 'fuse-carousel__overflow-container',
	})`
	display: flex;

	overflow-x: auto;
	scroll-snap-type: x mandatory;

	scroll-behavior: smooth;
	-webkit-overflow-scrolling: touch;

	scrollbar-width: none;
	-ms-overflow-style: none;
	&::-webkit-scrollbar {
		display: none;
	}

	${({ touchPeek, chevronOverlay }) => `
		${
	touchPeek
		? `
			margin-right: calc(-1 * ${gridConfig.xxs.margin});

			@media screen and (min-width: ${gridConfig.m.minWidth}) {
				// Chevron overlay does not have peek for tablet and desktop
				margin-right: ${chevronOverlay ? `0` : `calc(-1 * ${gridConfig.m.margin})`};
			}

			@media screen and (min-width: ${gridConfig.l.minWidth}) {
				margin-right: 0;
			}`
		: ``
};
	`};
`;

const StyledGridContainer = styled(GridContainer).withConfig({ shouldForwardProp: (prop) => prop !== 'gridContainerPadding' }).attrs({
	className: 'fuse-carousel__styled-grid-container',
})`
	@media screen and (min-width: ${gridConfig.l.minWidth}) {
		padding: ${(props) => (props.gridContainerPadding ? ' ' : '0')};
	}
`;

const Pip = styled.button.attrs({
	className: 'fuse-carousel__pip',
})`
	display: flex;
	background-color: var(--color-global-grey800);
	width: 44px;
	height: var(--spacing-global-space1);
	margin: 21px 4px;
	position: relative;
	border: 0;

	&.current {
		background-color: var(--color-brand-mid);
	}

	&:focus {
		outline: none;
	}

	&:focus-visible:after,
	&:active {
		${FocusCss}
	}

	&:focus:not(:focus-visible) {
		outline: none;
	}

	${TappableAreaCss}
	&:after {
		cursor: pointer;
	}
`;

const ButtonContainer = styled(GridItem).attrs({
	className: 'fuse-carousel__button-container',
})`
	place-items: center;
`;

const RenderIcon = (Icon) => renderToStaticMarkup(Icon);

const CarouselButton = styled.button.attrs({
	className: 'fuse-carousel__button'
})`
	&.fuse-carousel__button-previous {
			-webkit-box-pack: start;
			-ms-flex-pack: start;
			-webkit-justify-content: start;
			justify-content: start;
	}
	&.fuse-carousel__button-next {
			-webkit-box-pack: end;
			-ms-flex-pack: end;
			-webkit-justify-content: end;
			justify-content: end;
	}

	width: 44px;
	height: 44px;
	background: var(--color-global-white);
	display: inline-flex;
	border: 0;
	cursor: pointer;
	justify-content: center;
	align-items: center;
	padding-inline: 0;

	&:after {
		content: '';
		width: 32px;
		height: 32px;
		mask: url('data:image/svg+xml,${(props) => encodeURIComponent(RenderIcon(props.icon))}')
			center/cover no-repeat;
		-webkit-mask: url('data:image/svg+xml,${(props) => encodeURIComponent(RenderIcon(props.icon))}')
			center/cover no-repeat;
		background: var(--color-global-black);
		transition-duration: 300ms;
	}

	&:hover:not(:disabled):after {
		background: var(--color-brand-mid);
	}

	&:focus-visible:after {
		background: var(--color-brand-mid);
	}

	&:focus-visible,
	&:active:not(:disabled) {
		outline: var(--spacing-global-space1) solid var(--color-functional-focus);
	}

	&:active:not(:disabled) {
		background: var(--color-brand-mid);
	}

	&:active:not(:disabled):after {
		background: var(--color-global-white);
	}

	&:focus:not(:focus-visible) {
		outline: none;
	}

	&:disabled {
		opacity: 0.2;
		cursor: default;
	}

`;

const ChevronOverlayButton = styled(CarouselButton).attrs({
	className: `fuse-carousel__chevron-overlay-button`,
	'data-testid': `fuse-carousel__chevron-overlay-button`,
})`
	/* Do not display chevron overlay mobile */
	display: none;

	/* Only display chevron overlay on tablet and desktop */
	@media screen and (min-width: ${gridConfig.m.minWidth}) {
		${({ position }) => `
		display: inline-flex;
		position: absolute;
		${position}: 0;
		top: 50%;
		transform: translateY(-50%);
		z-index: 2;
	`}
	}
`;

const SlideContentContainer = styled.div
	.withConfig({
		shouldForwardProp: (prop) =>
			!['touchPeek', 'lSlides', 'mSlides', 'xxsSlides', 'chevronOverlay', 'columnGapSize'].includes(
				prop,
			),
	})
	.attrs({
		className: 'fuse-carousel__slide-content-container',
	})`
	${({ touchPeek, xxsSlides, mSlides, lSlides, chevronOverlay, columnGapSize }) => {
		const gapSizes = { ...defaultColumnGaps };

		if (columnGapSize) {
			Object.keys(columnGapSize).forEach((breakpoint) => {
				const gapSizeSpacingNumber = columnGapSize[breakpoint];

				if (gapSizeSpacingNumber && isColumnGapSizeValid(gapSizeSpacingNumber, breakpoint)) {
					gapSizes[breakpoint] = `var(--spacing-global-space${gapSizeSpacingNumber})`;
				}
			});
		}

		return `
		display: flex;
		flex-direction: column;
		position: relative;

		${
	touchPeek
		? `
					flex: 0 0 calc(${xxsSlides} * 95%);
					@media screen and (min-width: ${gridConfig.m.minWidth}) {
						flex: ${
	chevronOverlay
		? `0 0 calc(${mSlides}00% + (${mSlides - 1} * ${gapSizes.m}));`
		: `0 0 calc(${mSlides} * 95%);`
}
					}
				`
		: `
			flex: 0 0 calc(${xxsSlides}00% + (${xxsSlides - 1} * ${gapSizes.xxs}));

			@media screen and (min-width: ${gridConfig.m.minWidth}) {
				flex: 0 0 calc(${mSlides}00% + (${mSlides - 1} * ${gapSizes.m}));
			}
		`
}

		@media screen and (min-width: ${gridConfig.l.minWidth}) {
			flex: 0 0 calc(${lSlides}00% + (${lSlides - 1} * ${gapSizes.l}));
		}

		@media screen and (min-width: ${gridConfig.xl.minWidth}) {
			flex: 0 0 calc(${lSlides}00% + (${lSlides - 1} * ${gapSizes.xl}));
		}
	`;
	}}
`;

const CarouselSnapSection = styled(GridItem)
	.withConfig({ shouldForwardProp: (prop) => prop !== 'chevronOverlay' })
	.attrs({
		as: 'li',
		className: 'fuse-carousel__carousel-snap-section',
	})`
	scroll-snap-align: start;
	height: 1px;

	:first-of-type {
		grid-column-start: 1;
	}

	grid-column: auto / span 12;
	@media screen and (min-width: ${gridConfig.l.minWidth}) {
		${({ chevronOverlay }) =>
		chevronOverlay ? `grid-column: auto / span 12;` : `grid-column: auto / span 10;`}
	}
`;

const CarouselItemGrid = styled(GridRow)
	.withConfig({
		shouldForwardProp: (prop) =>
			!['columnGapSize', 'lSlides', 'mSlides', 'xxsSlides', 'chevronOverlay'].includes(prop),
	})
	.attrs({
		forwardedAs: 'ul',
		className: 'fuse-carousel__item-grid',
	})`
	&& {
		max-width: 100%;
		grid-row-gap: 0;

		${({ xxsSlides, mSlides, lSlides, chevronOverlay }) => `
			grid-template-columns: repeat(${12 * xxsSlides}, 1fr);

			@media screen and (min-width: ${gridConfig.m.minWidth}) {
				grid-template-columns: repeat(${12 * mSlides}, 1fr);

				.fuse-carousel__carousel-snap-section:nth-of-type(${mSlides}) ~ .fuse-carousel__carousel-snap-section {
					display: none;
				}
			}

			@media screen and (min-width: ${gridConfig.l.minWidth}) {
				grid-template-columns: ${
	chevronOverlay ? `repeat(${12 * lSlides}, 1fr)` : `repeat(${10 * lSlides}, 1fr)`
} ;

				.fuse-carousel__carousel-snap-section:nth-of-type(${lSlides}) ~ .fuse-carousel__carousel-snap-section {
					display: none;
				}
			}

			@media screen and (min-width: ${gridConfig.xl.minWidth}) {
				grid-template-columns: ${
	chevronOverlay ? `repeat(${12 * lSlides}, 1fr)` : `repeat(${10 * lSlides}, 1fr)`
} ;
			}
		`}

		${({ columnGapSize }) =>
		columnGapSize
			? Object.keys(columnGapSize)
				.map((breakpoint) => {
					const gapSizeSpacingNumber = columnGapSize[breakpoint];

					return (
						gridConfig[breakpoint] &&
								gapSizeSpacingNumber &&
								isColumnGapSizeValid(gapSizeSpacingNumber, breakpoint) &&
								`
									@media screen and (min-width: ${gridConfig[breakpoint].minWidth}) {
										column-gap: var(--spacing-global-space${gapSizeSpacingNumber});
									}`
					);
				})
				.filter(Boolean)
				.join('\n\r')
			: ''}
	}

	a {
		outline-offset: -2px;
	}

	.fuse-product-card:focus-visible {
		padding: 4px;
	}
`;

const PipContainer = styled.div
	.withConfig({
		shouldForwardProp: (prop) => !['lSlides', 'mSlides'].includes(prop),
	})
	.attrs({
		className: 'fuse-carousel__pip-container',
	})`
	display: flex;
	justify-content: center;
	${(props) => `
		@media screen and (min-width: ${gridConfig.m.minWidth}) {
			button:nth-child(${props.mSlides}) ~ * {
				display: none;
			}
		}

		@media screen and (min-width: ${gridConfig.l.minWidth}) {
			button:nth-child(${props.lSlides}) ~ * {
				display: none;
			}
		}
	`};
`;

const gridTemplateAreaChange = (prop) =>
	prop.title
		? `'. title title'
		 'prev slides next'
		 'pips pips pips'`
		: `'prev slides next' 'pips pips pips'`;

const CarouselLayout = styled(GridRow)
	.withConfig({ shouldForwardProp: (prop) => !['chevronOverlay', 'gridAreaOverride'].includes(prop), })
	.attrs({
		className: 'fuse-carousel__carousel-layout',
	})`
	position: relative;
	&& {
		grid-template-columns: 1fr 10fr 1fr;
		grid-template-areas: ${({ title }) =>
		title
			? `'title title title'
		'slides slides slides'
		'pips pips pips'`
			: `'slides slides slides'
		'pips pips pips'`};

		[class^='SrOnly'] {
			display: none;
		}

		@media screen and (max-width: ${gridConfig.m.minWidth}) {
			.SrOnlyXxs {
				display: inline;
			}
		}

		@media screen and (min-width: ${gridConfig.m.minWidth}) and (max-width: ${gridConfig.m.maxWidth}) {
			.SrOnlyM {
				display: inline;
			}

			${({ chevronOverlay, title }) =>
		chevronOverlay
			? `
						grid-template-columns: 12fr;
						grid-template-areas: ${
	title
		? `'title'
							'slides'
							'pips'`
		: `'slides'
							'pips'`
};
					`
			: `
					`}
		}

		@media screen and (min-width: ${gridConfig.l.minWidth}) {
			grid-template-columns: ${(props) => (props.gridAreaOverride ? '6rem 1fr 6rem;' : '1fr 10fr 1fr')};
			${({ chevronOverlay, title, gridAreaOverride }) =>
		chevronOverlay
			? `
						grid-template-columns: 12fr;
						grid-template-areas:
							${
	title
		? `'title'
							'slides'
							'pips'`
		: `'slides'
							'pips'`
};
					`
			: `
						grid-template-areas:
							${
	title && gridAreaOverride
		? `'title title title'
							'prev slides next'
							'pips pips pips'`
		: gridTemplateAreaChange({title})
};
					`}

			.SrOnlyL {
				display: inline;
			}
		}
	}
`;

export {
	CarouselContainer,
	OverflowContainer,
	Pip,
	ButtonContainer,
	CarouselButton,
	ChevronOverlayButton,
	SlideContentContainer,
	CarouselSnapSection,
	CarouselItemGrid,
	PipContainer,
	CarouselLayout,
	StyledGridContainer,
};
